import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import Layout from 'client/modules/App/components/Layout/LayoutContainer';
import TierPricingPage from 'client/modules/Public/Pricing/pages/PricingPage/TierPricingPage';
import callApi from 'client/util/apiCaller';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

const messages = defineMessages({
  title: {
    id: 'Billing.ChangePlan.Title',
    defaultMessage: 'Change plan',
  },
  back: {
    id: 'Billing.ChangePlan.Back',
    defaultMessage: 'Back to billing',
  },
  legacyPlanChange: {
    id: 'Billing.LegacyPlanChange',
    defaultMessage:
      'Plan changes are not available for legacy subscriptions. Please contact {support}.',
  },
  errorLoading: {
    id: 'Billing.Error',
    defaultMessage: 'Error loading billing information. Please try again.',
  },
});

function ChangePlanPage() {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    callApi(dispatch, 'users/subscriptions', null, 'GET')
      .then(ret => {
        if (!cancelled) {
          setSubscription(ret.subscription);
        }
      })
      .catch(err => {
        if (!cancelled) {
          if (err.status === 404) {
            setSubscription(null);
          } else {
            setError(err);
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const handlePlanChanged = useCallback(() => {
    navigate('/settings/billing');
  }, [navigate]);

  if (!loading && !subscription && !error) {
    return <Navigate to="/settings/billing" replace />;
  }

  return (
    <Layout
      title={formatMessage(messages.title)}
      returnLinkTo="/settings/billing"
      returnLinkText={formatMessage(messages.back)}
    >
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={400} height={24} />
          <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 1 }} />
        </Box>
      ) : error ? (
        <Card variant="outlined" sx={{ borderColor: 'error.main', my: 2 }}>
          <CardContent>
            <Typography variant="body1" color="error">
              {formatMessage(messages.errorLoading)}
            </Typography>
          </CardContent>
        </Card>
      ) : subscription?.legacy ? (
        <Card variant="outlined" sx={{ my: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage
                {...messages.legacyPlanChange}
                values={{
                  support: <a href="mailto:support@promoter.ninja">support@promoter.ninja</a>,
                }}
              />
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TierPricingPage
          changePlan
          currentPlanId={subscription.plan_id}
          onPlanChanged={handlePlanChanged}
        />
      )}
    </Layout>
  );
}

export default ChangePlanPage;
